// frontend/src/components/QuickAddBookmarkDialog.js

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/axiosConfig';

const QuickAddBookmarkDialog = ({ open, onClose, projectId, onBookmarkAdded }) => {
  const { currentUser } = useAuth();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [saveToProject, setSaveToProject] = useState('none');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingMetadata, setFetchingMetadata] = useState(false);
  const [error, setError] = useState('');

  // Fetch projects for dropdown
  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open]);

  const fetchProjects = async () => {
    try {
      const token = await currentUser.getIdToken();
      // Use dedicated projects endpoint rather than filtering sessions
      const response = await axios.get('/api/projects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Auto-fetch metadata when URL is entered
  const fetchMetadata = async (bookmarkUrl) => {
    if (!bookmarkUrl || !bookmarkUrl.startsWith('http')) return;
    
    setFetchingMetadata(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(
        '/api/bookmarks/extract-metadata',
        { url: bookmarkUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.title && !title) {
        setTitle(response.data.title);
      }
    } catch (error) {
      console.warn('Could not fetch metadata:', error);
    } finally {
      setFetchingMetadata(false);
    }
  };

  const handleUrlBlur = () => {
    if (url && url.startsWith('http')) {
      fetchMetadata(url);
    }
  };

  const handleSubmit = async () => {
    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();
      const bookmarkData = {
        url: url.trim(),
        title: title.trim() || url,
        tags: tags.split(',').map(tag => tag.trim()).filter(Boolean)
      };

      // Determine final projectId if provided and ensure it belongs to user
      let finalProjectId = null;
      if (saveToProject !== 'none') {
        finalProjectId = saveToProject;
      } else if (projectId) {
        finalProjectId = projectId;
      }

      if (finalProjectId) {
        const validProject = projects.find(p => p._id === finalProjectId);
        if (!validProject) {
          setError('Project not found.');
          setLoading(false);
          return;
        }
        bookmarkData.projectId = finalProjectId;
      }

      await axios.post('/api/bookmarks', bookmarkData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Success - reset form and close
      setUrl('');
      setTitle('');
      setTags('');
      setSaveToProject('none');
      setError('');
      onClose();
      
      // Show success message (could be a toast)
      console.log('Bookmark added successfully');
      
      // Notify parent component that bookmark was added
      if (onBookmarkAdded) {
        onBookmarkAdded();
      }
    } catch (error) {
      console.error('Error adding bookmark:', error);
      if (error.response?.status === 404 && error.response.data?.error === 'Project not found') {
        setError('Project not found.');
      } else {
        setError('Failed to add bookmark. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const handleClose = () => {
    setUrl('');
    setTitle('');
    setTags('');
    setSaveToProject('none');
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      onKeyDown={handleKeyDown}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📑 Add Bookmark
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="URL"
          fullWidth
          variant="outlined"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={handleUrlBlur}
          sx={{ mb: 2 }}
          required
          InputProps={{
            endAdornment: fetchingMetadata && <CircularProgress size={20} />
          }}
          placeholder="https://example.com"
        />
        
        <TextField
          margin="dense"
          label="Title (optional)"
          fullWidth
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Auto-filled from URL"
        />
        
        <TextField
          margin="dense"
          label="Tags (optional)"
          fullWidth
          variant="outlined"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="react, tutorial, javascript"
          helperText="Separate tags with commas"
        />
        
        <FormControl fullWidth variant="outlined">
          <InputLabel>Save to Project</InputLabel>
          <Select
            value={saveToProject}
            onChange={(e) => setSaveToProject(e.target.value)}
            label="Save to Project"
          >
            <MenuItem value="none">None (standalone bookmark)</MenuItem>
            {projects.map((project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading || !url.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Adding...' : 'Add Bookmark'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddBookmarkDialog;